import { createClient } from '@supabase/supabase-js'

// Use VITE_ prefix for public environment variables in a Vite project
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Check if credentials are available
const hasCredentials = supabaseUrl && supabaseAnonKey

if (!hasCredentials) {
  // Log warning instead of throwing error to allow app to load
  console.warn('⚠️  Supabase environment variables are missing!')
  console.warn('   Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in your .env.local file.')
  console.warn('   Get these from: https://app.supabase.com/project/_/settings/api')
  console.warn('   The app will continue to load, but Supabase features will not work until configured.\n')
}

// Security check: Detect if wrong key type is used
// Anon keys are JWTs starting with 'eyJ', NOT 'sb_secret_' or 'sb_publishable_'
// Note: JWT tokens can be longer than 200 chars, so we only check the prefix
if (hasCredentials && (supabaseAnonKey.startsWith('sb_secret_') || supabaseAnonKey.startsWith('sb_publishable_'))) {
  throw new Error(
    'SECURITY ERROR: You are using the wrong API key type! ' +
    'The key should be the "anon" or "public" key (starts with "eyJ"), not "sb_secret_" or "sb_publishable_".' +
    '\n\nTo fix this:\n' +
    '1. Go to: https://app.supabase.com/project/naognwyxpinkrwtpodix/settings/api\n' +
    '2. In "Project API keys" section, find the key labeled "anon" or "public"\n' +
    '3. Copy that key (it will start with "eyJhbGciOi..." - it\'s a JWT token)\n' +
    '4. Replace VITE_SUPABASE_ANON_KEY in your .env.local file\n' +
    '\nThe anon key is safe for browser use and respects Row Level Security (RLS).'
  )
}

// Additional check: Service role keys often contain 'service_role' in their claims
// This is a safety check to prevent accidental exposure
if (hasCredentials) {
  try {
    const keyParts = supabaseAnonKey.split('.')
    if (keyParts.length >= 2) {
      // Decode the payload (second part of JWT)
      const payload = JSON.parse(atob(keyParts[1]))
      if (payload.role === 'service_role') {
        throw new Error(
          'SECURITY ERROR: You are using a SERVICE ROLE KEY (secret key) in the browser. ' +
          'This is a critical security vulnerability! Service role keys bypass all security policies. ' +
          'Please immediately:\n' +
          '1. Remove the service role key from your .env file\n' +
          '2. Use the ANON KEY (public key) from Supabase Dashboard → Settings → API\n' +
          '3. Rotate your service role key if it was committed to version control'
        )
      }
    }
  } catch (e) {
    // Only ignore parsing errors (JSON.parse or atob failures)
    // Re-throw security errors that we intentionally threw
    if (e instanceof Error && e.message.includes('SECURITY ERROR')) {
      throw e
    }
    // If parsing fails (not a JWT or malformed), continue with normal validation
    // This is acceptable as the key format may vary
  }
}

// This client uses the public ANON key and is safe to use in the browser.
// Configure storage and auth settings for Chrome compatibility
// Use placeholder values if credentials are missing (allows app to load)
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsYWNlaG9sZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NDUxOTIwMDAsImV4cCI6MTk2MDc2ODAwMH0.placeholder',
  {
  auth: {
    // Use localStorage for session persistence (works better in Chrome)
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    // Auto-refresh session to keep user logged in
    autoRefreshToken: true,
    // Persist session across browser sessions
    persistSession: true,
    // Detect session from URL hash (for OAuth redirects)
    detectSessionInUrl: true,
    // Storage key prefix
    storageKey: 'supabase.auth.token',
    // Flow type - use PKCE for better security and Chrome compatibility
    flowType: 'pkce'
  },
  // Global settings
  global: {
    // Headers to include with all requests
    headers: {
      'X-Client-Info': 'lrdene-web'
    }
  }
})